import { TextField } from "@dash/form";
import type { FC } from "react";

import type { TicketCategory } from "@megaron/support-contracts";
import type { Uuid } from "@megaron/uuid";

import { SupportTicketInvoiceOrderFields } from "../invoice/SupportTicketInvoiceOrderFields";
import {
  SupportTicketProductsCreateField,
  TicketProductDraft,
} from "../products/SupportTicketProductsField";
import { SupportTicketCategorySelect } from "../shared/SupportTicketCategorySelect";
import { supportRequiredFieldLabel } from "../shared/supportRequiredFieldLabel";
import { SupportTicketReasonPicker } from "./SupportTicketReasonPicker";
import { SupportCreateTicketReporterField } from "./SupportCreateTicketReporterField";
import { SupportCreateTicketAssignedCustomersField } from "./SupportCreateTicketAssignedCustomersField";
import type { SupportCreateTicketContactOption } from "./supportTicketReasonCategories";
import { supportFormFieldCss } from "./supportCreateTicketFormStyles";

type Props = {
  message: string;
  setMessage: (value: string) => void;
  reporterFirstName: string;
  setReporterFirstName: (value: string) => void;
  reporterLastName: string;
  setReporterLastName: (value: string) => void;
  reporterEmail: string;
  setReporterEmail: (value: string) => void;
  selectedCustomerUuid: Uuid | undefined;
  setSelectedCustomerUuid: (value: Uuid | undefined) => void;
  selectedContact: string;
  onContactChange: (value: string) => void;
  contactOptions: SupportCreateTicketContactOption[];
  hasCustomerContext: boolean;
  assignedCustomerUuids: Uuid[];
  onAddAssignedCustomer: (customerUuid: Uuid) => void;
  onRemoveAssignedCustomer: (customerUuid: Uuid) => void;
  selectedReasonIds: string[];
  setSelectedReasonIds: (value: string[]) => void;
  category: TicketCategory | null;
  setCategory: (value: TicketCategory | null) => void;
  products: TicketProductDraft[];
  setProducts: (value: TicketProductDraft[]) => void;
  invoiceNumber: string;
  setInvoiceNumber: (value: string) => void;
  orderNumber: string;
  setOrderNumber: (value: string) => void;
};

export const SupportCreateTicketFormFields: FC<Props> = ({
  message,
  setMessage,
  reporterFirstName,
  setReporterFirstName,
  reporterLastName,
  setReporterLastName,
  reporterEmail,
  setReporterEmail,
  selectedCustomerUuid,
  setSelectedCustomerUuid,
  selectedContact,
  onContactChange,
  contactOptions,
  hasCustomerContext,
  assignedCustomerUuids,
  onAddAssignedCustomer,
  onRemoveAssignedCustomer,
  selectedReasonIds,
  setSelectedReasonIds,
  category,
  setCategory,
  products,
  setProducts,
  invoiceNumber,
  setInvoiceNumber,
  orderNumber,
  setOrderNumber,
}) => {
  return (
    <>
      <SupportTicketReasonPicker
        selectedReasonIds={selectedReasonIds}
        onSelectedReasonIdsChange={setSelectedReasonIds}
      />
      <SupportTicketCategorySelect variant="form" label="Kategoria" value={category} onChange={setCategory} />
      <SupportCreateTicketReporterField
        reporterFirstName={reporterFirstName}
        setReporterFirstName={setReporterFirstName}
        reporterLastName={reporterLastName}
        setReporterLastName={setReporterLastName}
        reporterEmail={reporterEmail}
        setReporterEmail={setReporterEmail}
        selectedCustomerUuid={selectedCustomerUuid}
        setSelectedCustomerUuid={setSelectedCustomerUuid}
        contactOptions={contactOptions}
        selectedContact={selectedContact}
        onContactChange={onContactChange}
        hasCustomerContext={hasCustomerContext}
      />
      <SupportCreateTicketAssignedCustomersField
        assignedCustomerUuids={assignedCustomerUuids}
        onAddCustomer={onAddAssignedCustomer}
        onRemoveCustomer={onRemoveAssignedCustomer}
      />
      <SupportTicketProductsCreateField value={products} onChange={setProducts} />
      <SupportTicketInvoiceOrderFields
        invoiceNumber={invoiceNumber}
        orderNumber={orderNumber}
        onInvoiceNumberChange={setInvoiceNumber}
        onOrderNumberChange={setOrderNumber}
        fieldClassName={supportFormFieldCss}
      />
      <TextField
        label={supportRequiredFieldLabel("Wiadomość")}
        value={message}
        onChange={setMessage}
        multiline
        css={supportFormFieldCss}
      />
    </>
  );
};
